// Import Libraries
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// Context
import SessionContext from '../context/Session';

const COPY = {
  // A signed-out visitor landing directly on a URL that requires an account
  // (e.g. a shotlist link) instead of silently bouncing them into the Auth0
  // redirect, which flashes an empty page while it kicks off.
  loggedOut: {
    heading: 'Log In to View This Project',
    body: 'Please log in with an account that has access to this project, or reach out to the script owner for an invite.',
    buttonLabel: 'Log In',
    destination: '/login',
  },
  // A signed-in visitor whose account isn't a collaborator on this
  // particular project (backend returned 403 resolving/activating it).
  noAccess: {
    heading: 'Log in to view this Project',
    body: 'Please log in with an account that has access to this project or reach out to the project owner for an invite.',
    buttonLabel: 'My Projects',
    destination: '/app/home',
  },
};

const AccessRequired = ({ variant = 'loggedOut' }) => {
  const navigate = useNavigate();
  const { setIsAccessRequired } = useContext(SessionContext);
  const { heading, body, buttonLabel, destination } = COPY[variant];

  // Help/Feedback point at authenticated-only resources, so hide them while
  // a signed-out visitor is looking at this page. The noAccess variant is for
  // a signed-in user, who can still use Help/Feedback normally.
  useEffect(() => {
    if (variant !== 'loggedOut') return undefined;
    setIsAccessRequired(true);
    return () => setIsAccessRequired(false);
  }, [variant, setIsAccessRequired]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 'calc(100vh - 65px)',
        gap: 3,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: '#EDEBFB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LockOutlinedIcon sx={{ color: '#7A64EB', fontSize: 32 }} />
      </Box>
      <Box>
        <Typography variant="h5" sx={{ color: '#1B134A', fontWeight: 800, mb: 1 }}>
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
          {body}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={() => navigate(destination)}
        sx={{
          textTransform: 'none',
          borderRadius: 9999,
          backgroundColor: '#7A64EB',
          '&:hover': { backgroundColor: '#6a55e0' },
        }}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
};

export default AccessRequired;
